import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import AdminLayout from './AdminLayout';
import './Referrals.css';

const Referrals = () => {
  const [referrals, setReferrals]       = useState([]);
  const [summary, setSummary]           = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId]     = useState(null);

  // Pay modal state
  const [payModal, setPayModal]         = useState(null); // referral object or null
  const [payAmount, setPayAmount]       = useState('');
  const [payRef, setPayRef]             = useState('');
  const [payNotes, setPayNotes]         = useState('');
  const [payProofFile, setPayProofFile] = useState(null);
  const [paying, setPaying]             = useState(false);
  const [payError, setPayError]         = useState('');

  useEffect(() => { fetchReferrals(); }, []);

  const fetchReferrals = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.get('/admin/referrals');
      setReferrals(Array.isArray(data?.referrals) ? data.referrals : []);
      setSummary(data?.summary || null);
      setError(null);
    } catch {
      setError('Failed to load referral data.');
      setReferrals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const fmtBonus = (v) => `£${parseFloat(v || 0).toFixed(2)}`;

  const visible = statusFilter === 'all'
    ? referrals
    : referrals.filter(r => r.status === statusFilter);

  const statusBadge = (s) => {
    const map = { pending: 'rf-badge--warning', approved: 'rf-badge--blue', paid: 'rf-badge--success' };
    return `rf-badge ${map[s] || 'rf-badge--secondary'}`;
  };

  const EligibilityCell = ({ r }) => {
    const referred = (() => {
      if (!r.referred_active)                  return { cls: 'rf-badge--danger',  text: 'Inactive' };
      if (!(r.referred_assigned_count > 0))    return { cls: 'rf-badge--danger',  text: 'No Student Assigned' };
      if (!(r.referred_paid_count > 0))        return { cls: 'rf-badge--warning', text: 'Student Not Paid' };
      return { cls: 'rf-badge--success', text: '✓ Eligible' };
    })();
    return <span className={`rf-badge ${referred.cls}`}>{referred.text}</span>;
  };

  const openPayModal = (r, e) => {
    e.stopPropagation();
    setPayAmount('');
    setPayRef('');
    setPayNotes('');
    setPayProofFile(null);
    setPayError('');
    setPayModal(r);
  };

  const handlePay = async () => {
    if (!payModal) return;
    const amount = parseFloat(payAmount);
    if (!payAmount || isNaN(amount) || amount <= 0) {
      setPayError('Please enter a valid payment amount greater than £0.');
      return;
    }
    setPaying(true);
    setPayError('');
    try {
      const fd = new FormData();
      fd.append('bonus_amount', amount.toFixed(2));
      if (payRef.trim())   fd.append('payment_reference', payRef.trim());
      if (payNotes.trim()) fd.append('notes', payNotes.trim());
      if (payProofFile)    fd.append('proofImage', payProofFile);
      await api.post(`/admin/referrals/${payModal.id}/pay`, fd);
      setPayModal(null);
      setPayProofFile(null);
      await fetchReferrals();
    } catch (err) {
      setPayError(err?.message || 'Payment failed. Please try again.');
    } finally {
      setPaying(false);
    }
  };

  const hasBankDetails = (r) => r.bank_holder || r.bank_name || r.bank_sort_code || r.bank_account_number;

  if (loading) return (
    <AdminLayout pageTitle="Referrals">
      <div className="rf-page">
        <div className="rf-loading">
          <div className="rf-spinner" />
          <p>Loading referral data…</p>
        </div>
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout pageTitle="Referrals">
      <div className="rf-page">
        {/* Header */}
        <div className="rf-header">
          <div className="rf-header-left">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            <div>
              <h1 className="rf-title">Referral Programme</h1>
              <p className="rf-subtitle">Track instructor referrals and bonus payments</p>
            </div>
          </div>
          <button className="rf-refresh-btn" onClick={fetchReferrals}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
            Refresh
          </button>
        </div>

        {error && <div className="rf-alert">{error}</div>}

        {/* KPI Cards */}
        <div className="rf-kpis">
          <div className="rf-kpi">
            <div className="rf-kpi-icon rf-kpi-icon--blue">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <div className="rf-kpi-body">
              <span className="rf-kpi-label">Total Referrals</span>
              <span className="rf-kpi-value">{summary?.total || 0}</span>
            </div>
          </div>
          <div className="rf-kpi">
            <div className="rf-kpi-icon rf-kpi-icon--amber">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <div className="rf-kpi-body">
              <span className="rf-kpi-label">Pending</span>
              <span className="rf-kpi-value">{summary?.pending || 0}</span>
            </div>
          </div>
          <div className="rf-kpi">
            <div className="rf-kpi-icon rf-kpi-icon--indigo">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
              </svg>
            </div>
            <div className="rf-kpi-body">
              <span className="rf-kpi-label">Approved</span>
              <span className="rf-kpi-value">{summary?.approved || 0}</span>
            </div>
          </div>
          <div className="rf-kpi">
            <div className="rf-kpi-icon rf-kpi-icon--green">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
            </div>
            <div className="rf-kpi-body">
              <span className="rf-kpi-label">Bonuses Paid</span>
              <span className="rf-kpi-value">{fmtBonus(summary?.total_paid)}</span>
              <span className="rf-kpi-sub">{summary?.paid || 0} paid out</span>
            </div>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="rf-controls">
          <div className="rf-filter-tabs">
            {['all', 'pending', 'approved', 'paid'].map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`rf-filter-tab${statusFilter === s ? ' rf-filter-tab--active' : ''}`}
              >
                {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                <span className="rf-tab-count">
                  {s === 'all' ? referrals.length : referrals.filter(r => r.status === s).length}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="rf-table-wrap">
          <table className="rf-table">
            <thead>
              <tr>
                <th>Referring Instructor</th>
                <th>New Applicant</th>
                <th>Code Used</th>
                <th>Amount Paid</th>
                <th>Status</th>
                <th>Eligibility</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {visible.length > 0 ? visible.map(r => (
                <React.Fragment key={r.id}>
                  <tr
                    className={`rf-row${expandedId === r.id ? ' rf-row--expanded' : ''}`}
                    onClick={() => setExpandedId(prev => prev === r.id ? null : r.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <span className="rf-name">{r.referring_name || '—'}</span>
                      <span className="rf-email">{r.referring_email || ''}</span>
                    </td>
                    <td>
                      <span className="rf-name">{r.referred_name || '—'}</span>
                      <span className="rf-email">{r.referred_email || ''}</span>
                    </td>
                    <td><span className="rf-code">{r.referral_code_used || '—'}</span></td>
                    <td className="rf-td-bonus">{r.status === 'paid' ? fmtBonus(r.bonus_amount) : '—'}</td>
                    <td>
                      <span className={statusBadge(r.status)}>
                        {r.status === 'paid' ? '✓ Paid' : r.status || 'pending'}
                      </span>
                    </td>
                    <td><EligibilityCell r={r} /></td>
                    <td className="rf-td-date">{fmt(r.created_at)}</td>
                    <td onClick={e => e.stopPropagation()}>
                      {r.status !== 'paid' ? (
                        <button
                          className="rf-pay-btn"
                          onClick={(e) => openPayModal(r, e)}
                        >
                          💳 Pay Bonus
                        </button>
                      ) : (
                        <span className="rf-paid-label">
                          ✓ Paid {fmt(r.paid_at)}
                        </span>
                      )}
                    </td>
                  </tr>

                  {/* Expanded bank details row */}
                  {expandedId === r.id && (
                    <tr className="rf-detail-row">
                      <td colSpan="8">
                        <div className="rf-detail-panel">
                          <div className="rf-detail-section">
                            <h4 className="rf-detail-heading">🏦 Instructor Bank Details</h4>
                            {hasBankDetails(r) ? (
                              <div className="rf-bank-grid">
                                {r.bank_holder && (
                                  <div className="rf-bank-field">
                                    <span className="rf-bank-label">Account Holder</span>
                                    <span className="rf-bank-value">{r.bank_holder}</span>
                                  </div>
                                )}
                                {r.bank_name && (
                                  <div className="rf-bank-field">
                                    <span className="rf-bank-label">Bank Name</span>
                                    <span className="rf-bank-value">{r.bank_name}</span>
                                  </div>
                                )}
                                {r.bank_sort_code && (
                                  <div className="rf-bank-field">
                                    <span className="rf-bank-label">Sort Code</span>
                                    <span className="rf-bank-value rf-bank-mono">{r.bank_sort_code}</span>
                                  </div>
                                )}
                                {r.bank_account_number && (
                                  <div className="rf-bank-field">
                                    <span className="rf-bank-label">Account Number</span>
                                    <span className="rf-bank-value rf-bank-mono">{r.bank_account_number}</span>
                                  </div>
                                )}
                                {r.bank_iban && (
                                  <div className="rf-bank-field">
                                    <span className="rf-bank-label">IBAN</span>
                                    <span className="rf-bank-value rf-bank-mono">{r.bank_iban}</span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p className="rf-no-bank">No banking details on file for this instructor.</p>
                            )}
                          </div>
                          {r.status === 'paid' && r.payment_reference && (
                            <div className="rf-detail-section">
                              <h4 className="rf-detail-heading">✅ Payment Completed</h4>
                              <div className="rf-bank-grid">
                                <div className="rf-bank-field">
                                  <span className="rf-bank-label">Reference</span>
                                  <span className="rf-bank-value rf-bank-mono">{r.payment_reference}</span>
                                </div>
                                <div className="rf-bank-field">
                                  <span className="rf-bank-label">Paid On</span>
                                  <span className="rf-bank-value">{fmt(r.paid_at)}</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )) : (
                <tr>
                  <td colSpan="8" className="rf-no-data">
                    {error ? 'Error loading data.' : 'No referrals found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {visible.length > 0 && (
          <div className="rf-table-footer">
            Showing {visible.length} record{visible.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* ── Pay Modal ── */}
      {payModal && (
        <div className="rf-modal-overlay" onClick={() => setPayModal(null)}>
          <div className="rf-modal" onClick={e => e.stopPropagation()}>
            <div className="rf-modal-header">
              <h2 className="rf-modal-title">💳 Pay Referral Bonus</h2>
              <button className="rf-modal-close" onClick={() => setPayModal(null)}>✕</button>
            </div>

            <div className="rf-modal-body">
              {/* Instructor info */}
              <div className="rf-modal-info-card">
                <div className="rf-modal-info-row">
                  <span className="rf-modal-info-label">Instructor</span>
                  <span className="rf-modal-info-value">{payModal.referring_name || '—'}</span>
                </div>
                <div className="rf-modal-info-row">
                  <span className="rf-modal-info-label">Email</span>
                  <span className="rf-modal-info-value">{payModal.referring_email || '—'}</span>
                </div>
              </div>

              {/* Payment amount (editable) */}
              <div className="rf-modal-field">
                <label className="rf-modal-label">Payment Amount <span style={{ color: '#e53e3e' }}>*</span></label>
                <div className="rf-modal-amount-wrap">
                  <span className="rf-modal-amount-prefix">£</span>
                  <input
                    className="rf-modal-input rf-modal-amount-input"
                    type="number"
                    min="0.01"
                    step="0.01"
                    placeholder="0.00"
                    value={payAmount}
                    onChange={e => setPayAmount(e.target.value)}
                  />
                </div>
                <p className="rf-modal-file-hint">Enter the amount you are paying this instructor for their referral.</p>
              </div>

              {/* Bank details */}
              <div className="rf-modal-bank">
                <h4 className="rf-modal-bank-heading">🏦 Bank Details</h4>
                {hasBankDetails(payModal) ? (
                  <div className="rf-modal-bank-grid">
                    {payModal.bank_holder && <div className="rf-mb-field"><span>Account Holder</span><strong>{payModal.bank_holder}</strong></div>}
                    {payModal.bank_name && <div className="rf-mb-field"><span>Bank</span><strong>{payModal.bank_name}</strong></div>}
                    {payModal.bank_sort_code && <div className="rf-mb-field"><span>Sort Code</span><strong className="rf-bank-mono">{payModal.bank_sort_code}</strong></div>}
                    {payModal.bank_account_number && <div className="rf-mb-field"><span>Account No.</span><strong className="rf-bank-mono">{payModal.bank_account_number}</strong></div>}
                    {payModal.bank_iban && <div className="rf-mb-field"><span>IBAN</span><strong className="rf-bank-mono">{payModal.bank_iban}</strong></div>}
                  </div>
                ) : (
                  <p className="rf-no-bank">⚠️ No banking details on file. Contact instructor before paying.</p>
                )}
              </div>

              {/* Proof of payment upload */}
              <div className="rf-modal-field">
                <label className="rf-modal-label">Proof of Payment (optional)</label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  className="rf-modal-file-input"
                  onChange={e => setPayProofFile(e.target.files[0] || null)}
                />
                {payProofFile && (
                  <div className="rf-modal-file-name">📎 {payProofFile.name}</div>
                )}
                <p className="rf-modal-file-hint">Upload a screenshot or PDF of the bank transfer — this will be emailed to the instructor as proof.</p>
              </div>

              {/* Payment reference */}
              <div className="rf-modal-field">
                <label className="rf-modal-label">Payment Reference (optional)</label>
                <input
                  className="rf-modal-input"
                  type="text"
                  placeholder="e.g. bank transfer ref, Faster Payment ref…"
                  value={payRef}
                  onChange={e => setPayRef(e.target.value)}
                />
              </div>

              <div className="rf-modal-field">
                <label className="rf-modal-label">Notes (optional)</label>
                <textarea
                  className="rf-modal-input rf-modal-textarea"
                  placeholder="Any internal notes about this payment…"
                  value={payNotes}
                  onChange={e => setPayNotes(e.target.value)}
                  rows={2}
                />
              </div>

              {payError && <div className="rf-modal-error">⚠️ {payError}</div>}

              <div className="rf-modal-notice">
                This will mark the referral as <strong>Paid</strong> and record a <strong>£{parseFloat(payAmount || 0).toFixed(2)}</strong> deduction in the Revenue tab.
              </div>
            </div>

            <div className="rf-modal-footer">
              <button className="rf-modal-cancel" onClick={() => setPayModal(null)} disabled={paying}>
                Cancel
              </button>
              <button className="rf-modal-confirm" onClick={handlePay} disabled={paying}>
                {paying ? 'Processing…' : `Confirm Payment of £${parseFloat(payAmount || 0).toFixed(2)}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Referrals;
